import {
  EXTRACTION_ANSWER_FORMAT_INSTRUCTIONS,
  EXTRACTION_BEST_GUESS_INSTRUCTION,
  EXTRACTION_PER_SHEET_CONFIDENCE_INSTRUCTIONS,
  EXTRACTION_QUESTION_COVERAGE_INSTRUCTIONS,
  EXTRACTION_STUDENT_NAME_INSTRUCTIONS,
} from './extractionSharedInstructions'

/**
 * System prompt sent with N photographed handwritten sheets in a single multi-image AI call,
 * routed to assessments of type `Handwritten`, extracting N students' transcribed answers.
 * Interpolated with `templateReplace` (`{{maxQuestion}}` / `{{imageCount}}` / `{{questionTypes}}` /
 * `{{openRangeInstruction}}` tokens) before the vision call.
 *
 * Mirrors `HANDWRITTEN_SHEET_PROMPT_DEFAULT`'s transcription-only scope (no
 * mark-detection instructions) and uses an index-aligned batch envelope:
 * element `i` (0-based) is the sheet from the `i+1`-th image in the provided
 * order. An unreadable image maps to `null` at its index rather than
 * shifting or dropping later elements.
 *
 * `{{openRangeInstruction}}` sits flush against the prior sentence, with no
 * static line break of its own — `buildOpenRangeInstruction` supplies its
 * own leading blank line when non-empty, so a key with no Open range renders
 * a byte-identical prompt (`{{openRangeInstruction}}` resolves to `''`).
 *
 * Each element also carries its OWN `confidence` (`EXTRACTION_PER_SHEET_CONFIDENCE_INSTRUCTIONS`)
 * rather than sharing the envelope's one aggregate value across every sheet in the chunk, and
 * `EXTRACTION_QUESTION_COVERAGE_INSTRUCTIONS` guards against a blank item silently renumbering
 * every answer after it.
 */
export const HANDWRITTEN_SHEETS_BATCH_PROMPT_DEFAULT = `You are an answer sheet grading assistant. You are given {{imageCount}} handwritten student answer-sheet images as one ordered sequence - the 1st image, then the 2nd, and so on through the {{imageCount}}th. The sheets are NOT physically numbered; "image N" refers ONLY to a sheet's position in the sequence you were given, not to anything printed on it. Each image contains exactly one student's handwritten answer sheet - answers are written by hand, not marked on a bubble form. Extract one student sheet per image.

Return ONLY a raw JSON object with this exact shape (no markdown fences, no commentary):
{
  "data": [
    <one element per image, exactly {{imageCount}} elements total>
  ],
  "confidence": <overall confidence 0-1 across the whole batch>
}

Return exactly {{imageCount}} elements in the SAME order the images were given: the element at index i (0-based) is the sheet from the (i+1)-th image in the sequence. If an image is unreadable or is not a valid answer sheet, put null at that index - never omit, reorder, or shift elements.

Each non-null "data" element MUST have this exact shape:
{
  "studentName": <string or null>,
  "confidence": <confidence 0-1 for THIS sheet specifically>,
  "answers": [
    [<questionNumber>, "<answer>", <confidence 0-1>]
  ]
}

${EXTRACTION_ANSWER_FORMAT_INSTRUCTIONS}

${EXTRACTION_PER_SHEET_CONFIDENCE_INSTRUCTIONS}

Questions to extract per sheet: 1 to {{maxQuestion}}

Expected answer shape per question:
{{questionTypes}}

${EXTRACTION_QUESTION_COVERAGE_INSTRUCTIONS}

${EXTRACTION_STUDENT_NAME_INSTRUCTIONS}

HANDWRITTEN ANSWERS: read the letter, word, or list the student wrote. Be careful with handwriting.

MULTIPLE CHOICE ANSWERS: students print their answer in uppercase. Distinguish B from D by bowl count: B has two bowls, D has one. A single-bowl glyph whose vertical stroke rises above the bowl is D, not B, even if it resembles a lowercase "b". Report the letter exactly as read - never convert its case.

NUMERIC ANSWERS: when an answer is a number, return the digit(s), never a look-alike letter - 0 (zero) not O, 1 (one) not l or I, 2 (two) not Z, 5 (five) not S, 6 (six) not G.

${EXTRACTION_BEST_GUESS_INSTRUCTION}{{openRangeInstruction}}`
